import { Injectable } from "@angular/core";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { merge, of } from "rxjs";
import { catchError, filter, map, switchMap } from "rxjs/operators";

import { AppContextSelector } from "../app-context/app-context.selector";
import { AppError } from "../app-error/app-error.model";
import { AUTH_ACTION_TYPE } from "../auth/auth.action";
import { AuthSelector } from "../auth/auth.selector";
import { CUSTOMER_MANAGEMENT_ACTION_TYPE } from "../customer-management/customer-management.action";
import { Customer } from "../customer-management/customer-management.model";
import { MarketSelector } from "../market/market.selector";
import { MessageService } from "../message/message.service";
import { CoreState } from "../state";

import { CURRENCY_ACTION_TYPE, CurrencyAction } from "./currency.action";
import { CURRENCY_MESSAGE_CONTEXT } from "./currency.const";
import { CurrencyService } from "./currency.service";

/**
 * @deprecated -[fabric-deprecated] - Use currency-service from app-wallet instead.
 * */
@Injectable()
export class CurrencyEffect {
	initializeCurrencyCode$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CURRENCY_ACTION_TYPE.initialize),
			concatLatestFrom(() => [
				this.store.select(AuthSelector.isAuthenticated),
				this.store.select(this.marketSelector.getCurrent()),
				this.store.select(AppContextSelector.getCountryCode),
			]),
			filter(([, isAuthenticated]) => !isAuthenticated),
			map(([, , currentMarket, startupContextCountry]) => {
				if (currentMarket.languageCode === "en" && startupContextCountry === "GB") {
					return this.service.getCurrencyCodeByCountryCode(startupContextCountry);
				}
				return currentMarket.currency;
			}),
			switchMap((startupContextCurrency) =>
				this.service.initialize(startupContextCurrency).pipe(
					map(() => this.action.initializeSuccess()),
					catchError((error) => of(this.action.initializeFail(error)))
				)
			)
		)
	);

	/**
	 * @deprecated - [fabric-deprecated] - Use WalletAppCommand.SetCurrentCurrencyCode instead
	 * */
	setCurrencyCode$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CURRENCY_ACTION_TYPE.set),
			map((x: ActionWithPayload) => x.payload),
			switchMap((currencyCode) =>
				this.service.set(currencyCode).pipe(
					map(() => this.action.setSuccess()),
					catchError((error) => of(this.action.setFail(error)))
				)
			)
		)
	);

	endSession$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionEnd),
			concatLatestFrom(() => [
				this.store.select(this.marketSelector.getCurrent()),
				this.store.select(AppContextSelector.getCountryCode),
			]),
			map(([, currentMarket, startupContextCountry]) => {
				if (currentMarket.languageCode === "en" && startupContextCountry === "GB") {
					return this.service.getCurrencyCodeByCountryCode(startupContextCountry);
				}
				return currentMarket.currency;
			}),
			switchMap((startupContextCurrency) =>
				this.service.set(startupContextCurrency).pipe(
					map(() => this.action.setSuccess()),
					catchError((error: AppError) => of(this.action.setFail(error)))
				)
			)
		)
	);

	setCustomerCurrencyCode$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				CUSTOMER_MANAGEMENT_ACTION_TYPE.getCustomerSuccess,
				CUSTOMER_MANAGEMENT_ACTION_TYPE.updateCustomerSuccess
			),
			map((x: ActionWithPayload<Customer>) => x.payload),
			switchMap((customer) =>
				this.service.set(customer.basicInformation.currencyCode).pipe(
					map(() => this.action.setCustomerCurrencySuccess()),
					catchError((error: AppError) => of(this.action.setCustomerCurrencyFail(error)))
				)
			)
		)
	);
	/**
	 * @deprecated - [fabric-deprecated] - Use WalletAppCommand.GetAllCurrencies instead
	 */
	getAll$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CURRENCY_ACTION_TYPE.getAll),
			switchMap(() =>
				this.service.getCurrencies().pipe(
					map((response) => this.action.getAllSuccess(response)),
					catchError((error: AppError) => of(this.action.getAllFail(error)))
				)
			)
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$({
				[CURRENCY_ACTION_TYPE.getAllFail]: CURRENCY_MESSAGE_CONTEXT.getAll,
			}),
			this.messageService.mapClearByContext$({
				[CURRENCY_ACTION_TYPE.getAll]: CURRENCY_MESSAGE_CONTEXT.getAll,
			})
		)
	);

	constructor(
		private action: CurrencyAction,
		private service: CurrencyService,
		private actions$: Actions,
		private store: Store<CoreState>,
		private messageService: MessageService,
		private marketSelector: MarketSelector
	) {}
}
